import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MapPin, Briefcase, Users, Upload, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { API } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";

export default function Careers() {
  const { t } = useLanguage();
  const [jobs, setJobs] = useState(null);
  const [applyJob, setApplyJob] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/jobs`).then((r) => setJobs(r.data)).catch(() => setJobs([]));
  }, []);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submitApplication = async (e) => {
    e.preventDefault();
    if (!resume) {
      toast.error(t.careers.resume);
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("job_id", applyJob.id);
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("message", form.message);
      fd.append("resume", resume);
      await axios.post(`${API}/applications`, fd);
      toast.success(t.careers.success);
      setApplyJob(null);
      setForm({ name: "", email: "", phone: "", message: "" });
      setResume(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || t.careers.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="careers-page" className="min-h-screen bg-background text-foreground">
      <Navbar />
      <section className="border-b border-border bg-muted/40">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
          <span className="inline-flex items-center gap-2 rounded-full border border-rose-500/40 bg-rose-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-rose-500">
            <Briefcase className="h-3.5 w-3.5" />
            {t.careers.badge}
          </span>
          <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
            {t.careers.heading}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground sm:text-lg">
            {t.careers.subheading}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6" data-testid="jobs-list-container">
        {!jobs ? (
          <p className="text-sm text-muted-foreground">{t.admin.loading}</p>
        ) : jobs.length === 0 ? (
          <p data-testid="no-jobs-message" className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            {t.careers.noJobs}
          </p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {jobs.map((job, i) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 2) * 0.08 }}
                data-testid={`job-card-${job.id}`}
                className="rounded-2xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-lg"
              >
                <h3 className="font-heading text-xl font-bold">{job.title}</h3>
                <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-rose-600" />{job.location}</span>
                  <span className="flex items-center gap-1.5"><Briefcase className="h-4 w-4 text-rose-600" />{job.type}</span>
                  <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-rose-600" />{job.openings} {t.careers.openings}</span>
                </div>
                {job.description && (
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{job.description}</p>
                )}
                <Button className="mt-5 gap-2" data-testid={`apply-button-${job.id}`} onClick={() => setApplyJob(job)}>
                  <Send className="h-4 w-4" /> {t.careers.apply}
                </Button>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <Dialog open={!!applyJob} onOpenChange={(o) => !o && setApplyJob(null)}>
        <DialogContent data-testid="application-dialog" className="max-w-lg" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {t.careers.applyFor}: {applyJob?.title}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitApplication} className="grid gap-4" data-testid="application-form">
            <div className="grid gap-2">
              <Label>{t.enquiry.name} *</Label>
              <Input required value={form.name} onChange={update("name")} data-testid="applicant-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{t.admin.email} *</Label>
                <Input required type="email" value={form.email} onChange={update("email")} data-testid="applicant-email-input" />
              </div>
              <div className="grid gap-2">
                <Label>{t.enquiry.phone} *</Label>
                <Input required value={form.phone} onChange={update("phone")} data-testid="applicant-phone-input" />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>{t.careers.coverNote}</Label>
              <Textarea rows={3} value={form.message} onChange={update("message")} data-testid="applicant-message-input" />
            </div>
            <div className="grid gap-2">
              <Label>{t.careers.resume} *</Label>
              <label
                className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground hover:border-rose-500/50"
                data-testid="resume-upload-label"
              >
                <Upload className="h-4 w-4" />
                {resume ? resume.name : t.careers.chooseFile}
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  data-testid="resume-file-input"
                  onChange={(e) => setResume(e.target.files?.[0] || null)}
                />
              </label>
            </div>
            <Button type="submit" disabled={submitting} className="gap-2" data-testid="application-submit-button">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {t.careers.submit}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
